import { Close, Edit, Save } from "@mui/icons-material";
import { Button, Divider, IconButton, InputAdornment, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useContext, useState } from "react";
import sectionTypes from "../../../config/sectionTypes";
import modes from "../../../config/modes.json";
import views from "../../../config/views.json";
import UserContext from "../../../services/Context/UserContext"
import Toggle from "../../Inputs/Toggle"
import Wysiwyg from "../../Inputs/Wysiwyg";
import PDF from "../../Inputs/PDF";
import { useEffect } from "react";
import API from "../../../services/API";
import Drop from "../../Inputs/Drop";
import GoogleDoc from "../../Tools/GoogleDoc";
import Category from "../../Inputs/Category";
import ToastContext from "../../../services/Context/ToastContext";
import { Link, useNavigate, useParams } from "react-router-dom";
import useUpdateSection from "../Hooks/useUpdateSection";
import VimeoUpload from "./VimeoUpload";
import { txt } from "../../../services/translation";
import Notion from "./Notion";

export default function SectionForm({section, onSubmit, onCancel}){

    const {id} = useParams()

    const navigate = useNavigate()

    const {setToast} = useContext(ToastContext)

    const updateSection = useUpdateSection()

    const [data, setData] = useState(null)

    const {user} = useContext(UserContext)

    const [file, setFile] = useState(null)
    
    const [uploading, setUploading] = useState(false)

    const [errors, setErrors] = useState({})

    useEffect(() => {

        setData({...section})

        if(section.type?.id === 'pdf'){
            API.downloadInUrl(`/design/sections/${section.id}/doc.pdf`)
                .then(url => setFile({url}))
        }

        if(section.type?.id === 'download'){
            API.downloadInUrl(`/design/sections/${section.id}/doc.zip`)
                .then(url => setFile({url}))
        }

    }, [section])

    const handleSubmit = e => {
        e.preventDefault()
        updateSection(data)
        return onSubmit(data)
    }

    const type = sectionTypes.find(t => t.id === data?.type?.id)

    const typeIs = str => str === type?.id

    const typeIn = arr => arr.includes(type?.id)

    const sx = {
        margin: 'auto', 
        p: 1,
        maxWidth: typeIn(['html']) ? null : 600
    }

    const handleUpload = f => {
        if(!f) return setFile(null);

        setFile(null)
        setUploading(true)
        setErrors({...errors, file: false})

        API.upload(`/design/sections/${data.id}/upload`, f)
            .then(r => {
                setFile(f)
                setData({...data, media: r.data?.name})
            })
            .catch(e => setErrors({...errors, file: txt('imageTooHeavyOrWrongFormat')}))
            .finally(() => setUploading(false))
    }


    const handleQuizPassMark = e => {
        let value = e.target.value;
        if(!value) return setData({...data, quizPassMark: value})
        value = parseInt(value);
        value = value < 0 ? 0 : value;
        value = value > 100 ? 100 : value;
        return setData({...data, quizPassMark: value})
    }


    const handleQuizEdit = () => {
        updateSection(data)
        return navigate(`/design/${id}/quizzes/${data.id}`)
    }

    
    return data && <form onSubmit={handleSubmit}>

        <Stack spacing={2} sx={sx}>

                <Stack direction='row' justifyContent='flex-end'>
                    <IconButton size='small' color='primary' type='submit'>
                        <Save/>
                    </IconButton>

                    <IconButton size='small' color='error' onClick={onCancel}>
                        <Close/>
                    </IconButton>
                </Stack>

                <TextField
                    label={txt('sectionType')}
                    select
                    required
                    size='small'
                    value={data.type?.id}
                    onChange={e => setData({...data, type: 
                        sectionTypes.find(t => t.id === e.target.value),
                        media: null,
                        quizPassMark: e.target.value !== 'quiz' ? null : 50
                    })}
                >
                    {sectionTypes
                        .filter(t => user.admin || t.public)
                        .sort((a,b) => a.title > b.title ? 1 : -1)
                        .map(t => 
                            <MenuItem value={t.id} key={t.id}>
                                <Stack direction='row' spacing={2}>
                                    {t.icon}
                                    <Typography>{t.title}</Typography>
                                </Stack>
                            </MenuItem>    
                        )
                    }
                </TextField>

                {typeIs('html') && 
                    <Wysiwyg 
                        value={data.html || ''}
                        onChange={html => setData({...data, html})}
                        onImageUpload={file => API.upload(`/pictures`, file)
                            .catch(e => setToast({
                                severity: 'error',
                                message: txt('imageTooHeavyOrWrongFormat')
                            }))
                        }
                    />
                }

                {typeIs('custom') && 
                    <textarea
                        placeholder="Code HTML à insérer dans la balise <body />"
                        value={data.html || ''}
                        onChange={event => setData({...data, html: event.target.value})}
                        style={{ 
                            minHeight: 300, 
                            backgroundColor: 'rgb(240,240,240)',
                            fontFamily: ['Courier new', 'sans-serif'],
                        }}
                    />
                }

                {typeIs('pdf') && 
                    <PDF
                        id={'pdf' + data.id}
                        value={file}
                        onChange={handleUpload}
                        loading={uploading}
                        error={errors?.file}
                    />
                }

                {typeIs('download') && 
                    <Drop
                        id={'zip' + data.id}
                        label={txt('uploadZipFileLabel')}
                        value={file}
                        onChange={handleUpload}
                        loading={uploading}
                        error={errors?.file}
                        accept={[
                            'application/zip', 
                            'application/x-zip-compressed', 
                            'application/x-compressed',
                            'application/x-zip'
                        ]}
                    />
                }

                {typeIs('video') &&
                    <VimeoUpload 
                        id={data.id}
                        onChange={video => setData({...data, media: video.id})}
                    />
                }

                {typeIs('google') && 
                    <GoogleDoc url={data.media} />
                }

                {typeIs('notion') && 
                    <Notion
                        value={data.notion} 
                        onChange={notion => setData({...data, notion, media: notion.id})}
                    />
                }

                {typeIs('quiz') && <>

                    <TextField 
                        label={txt('quizPassMarkLabel')}
                        type="number"
                        value={data.quizPassMark === 0 ? 0 : (data.quizPassMark || '')}
                        onChange={handleQuizPassMark}
                        inputProps={{min: 0, max: 100, step: 1}}
                        InputProps={{
                            endAdornment: <InputAdornment position='end'>%</InputAdornment>
                        }}
                        required
                        size="small"
                    />

                    <Button startIcon={<Edit />} variant="outlined" onClick={handleQuizEdit}>
                        {txt('editQuizButton')}
                    </Button>

                </>}

                {typeIn(['audit_before','audit_after','audit_reco']) && 
                    <Button 
                        startIcon={<Edit />} 
                        variant="outlined" 
                        component={Link} 
                        to={`/design/${id}/audit`}
                    >
                        Éditer la grille d'audit
                    </Button>
                }

                {typeIs('view') &&
                    <TextField
                        label={txt('view')}
                        select
                        size='small'
                        value={data.media || ''}
                        onChange={e => setData({...data, media: e.target.value})}
                    >
                        {views
                            .sort((a,b) => a.title > b.title ? 1 : -1)
                            .map(v => 
                                <MenuItem value={v.id} key={v.id}>
                                    {v.title}
                                </MenuItem>    
                            )
                        }
                    </TextField>
                }

                <Stack>

                    <Toggle 
                        label={txt('requiredLabel')}
                        value={data.required}
                        onChange={(e,v) => setData({...data, required: v})}
                    />

                    <Toggle 
                        label={txt('incrementCompletionLabel')}
                        value={data.incrementCompletion}
                        onChange={(e,v) => setData({...data, incrementCompletion: v})}
                    />

                </Stack>


                {user.admin && <>
                    
                    <Divider />

                    {typeIn(['video','google','youtube']) && 
                        <TextField 
                            label={type?.mediaLabel}
                            value={data.media || ''}
                            onChange={e => setData({...data, media: 
                                e.target.value.trim()
                            })}
                            required
                            size='small'
                        />
                    }

                    <TextField
                        label="Afficher pour"
                        select
                        size='small'
                        value={data.process || 'all'}
                        onChange={e => setData({...data, process: 
                            e.target.value === 'all' ? null : e.target.value
                        })}
                    >
                        <MenuItem value='all'>Tous</MenuItem>
                        {modes
                            .sort((a,b) => a.title > b.title ? 1 : -1)
                            .map(m => 
                                <MenuItem value={m.id} key={m.id}>
                                    {m.title}
                                </MenuItem>    
                            )
                        }
                    </TextField>

                    <TextField
                        label="Masquer pour"
                        select
                        size='small'
                        value={data.processOut || 'all'}
                        onChange={e => setData({...data, processOut: 
                            e.target.value === 'all' ? null : e.target.value
                        })}
                    >
                        <MenuItem value='all'>Personne</MenuItem>
                        {modes
                            .sort((a,b) => a.title > b.title ? 1 : -1)
                            .map(m => 
                                <MenuItem value={m.id} key={m.id}>
                                    {m.title}
                                </MenuItem>    
                            )
                        }
                    </TextField>

                    <Category 
                        value={data.category}
                        onChange={category => setData({...data, category})}
                    />

                </>}

        </Stack>

    </form>
}